import { Button, Grid, TextField } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IOfficialStaffList } from '../types';

type UpdateStaffProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  staff: IOfficialStaffList | null;
};

type FormValues = {
  name: string;
  designation: string;
  priority: string | number;
  image: FileList;
};

function UpdateStaff({
  handleClose,
  setRefreshTrigger,
  staff,
}: UpdateStaffProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      name: staff?.name || '',
      designation: staff?.designation || '',
      priority: staff?.priority || '',
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  // Sync form when `staff` changes (e.g. when opening modal with a new row)
  useEffect(() => {
    if (staff) {
      reset({
        name: staff.name || '',
        designation: staff.designation || '',
        priority: staff.priority || '',
      });
    }
  }, [staff, reset]);

  const onSubmit = async (values: FormValues) => {
    if (!staff?.id) return;

    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('designation', values.designation);
      formData.append('priority', String(values.priority || '0'));

      // Only send new image if selected
      if (values.image && values.image[0]) {
        formData.append('image', values.image[0]);
      }

      const { data } = await axios.patch(
        `${url}/api/v1/admin/office-staff/${staff.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Grid container px={1} spacing={1}>
        {/* Staff Name */}
        <Grid item xs={12} md={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='name'
            label='Staff Name'
            type='text'
            autoFocus
            variant='outlined'
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Staff name is required' })}
          />
        </Grid>

        {/* Designation */}
        <Grid item xs={12} md={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='designation'
            label='Designation'
            type='text'
            variant='outlined'
            error={!!errors.designation}
            helperText={errors.designation?.message}
            {...register('designation', {
              required: 'Designation is required',
            })}
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} md={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='priority'
            label='Priority'
            type='number'
            inputProps={{
              min: 0,
              step: '0.1',
            }}
            variant='outlined'
            error={!!errors.priority}
            helperText={errors.priority?.message}
            {...register('priority', { required: 'Priority is required' })}
          />
        </Grid>

        {/* Image Upload (optional in update) */}
        <Grid item xs={12} md={6}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Upload image'
            fieldName='image'
            hideCustomLabel
            onlyImage
            mode='single'
            // show existing image if available
            imgSerUrl={staff?.image ? [staff.image] : []}
          />
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UpdateStaff;
